/** Window event: the server refused a press because its button was out of date. */
export const PLANNING_ACTIONS_REFRESH_EVENT = 'refresh_planning_actions';

/** Error code the API gives a press whose button no longer matches the project. */
export const ACTION_STALE_CODE = 'ACTION_STALE';

/** Refusal codes whose message the API wrote for the user; any other failure keeps the generic text. */
export const EXPLAINED_REFUSAL_CODES = new Set([ACTION_STALE_CODE, 'ACTION_BLOCKED', 'ACTION_REFUSED']);

/**
 * Re-read the planning chat and its task rows in place, so the current next step
 * replaces the out-of-date button without reloading the page.
 */
export const refreshPlanningActions = (projectId: string): void => {
  window.dispatchEvent(new globalThis.CustomEvent(PLANNING_ACTIONS_REFRESH_EVENT, { detail: { projectId } }));
  window.dispatchEvent(new globalThis.CustomEvent('refresh_tasks'));
};

interface StartFailureCopy {
  notStarted: string;
  refreshed: string;
  titleSaved: string;
  selectRefresh: string;
}

/** The page's own words around the server's sentence, in the language the server wrote it in. */
const START_FAILURE_COPY: Record<string, StartFailureCopy> = {
  en: {
    notStarted: 'Planning didn’t start.',
    refreshed: 'The actions were refreshed: use the current next step.',
    titleSaved: 'Your title is saved, but planning could not start.',
    selectRefresh: 'Select Refresh actions to load the latest next step.',
  },
  es: {
    notStarted: 'La planificación no se inició.',
    refreshed: 'Las acciones se actualizaron: usa el siguiente paso actual.',
    titleSaved: 'Tu título está guardado, pero la planificación no pudo iniciarse.',
    selectRefresh: 'Selecciona Refresh actions para cargar el siguiente paso más reciente.',
  },
  pt: {
    notStarted: 'O planejamento não começou.',
    refreshed: 'As ações foram atualizadas: use o próximo passo atual.',
    titleSaved: 'Seu título foi salvo, mas o planejamento não pôde começar.',
    selectRefresh: 'Selecione Refresh actions para carregar o próximo passo mais recente.',
  },
  fr: {
    notStarted: 'La planification n’a pas démarré.',
    refreshed: 'Les actions ont été actualisées : utilisez l’étape suivante actuelle.',
    titleSaved: 'Votre titre est enregistré, mais la planification n’a pas pu démarrer.',
    selectRefresh: 'Sélectionnez Refresh actions pour charger la dernière étape suivante.',
  },
  ar: {
    notStarted: 'لم يبدأ التخطيط.',
    refreshed: 'تم تحديث الإجراءات: استخدم الخطوة التالية الحالية.',
    titleSaved: 'تم حفظ العنوان، لكن تعذّر بدء التخطيط.',
    selectRefresh: 'اختر Refresh actions لتحميل أحدث خطوة تالية.',
  },
  hi: {
    notStarted: 'प्लानिंग शुरू नहीं हुई।',
    refreshed: 'एक्शन रीफ़्रेश हो गए हैं: अभी का अगला चरण इस्तेमाल करें।',
    titleSaved: 'आपका शीर्षक सेव हो गया है, लेकिन प्लानिंग शुरू नहीं हो सकी।',
    selectRefresh: 'नया अगला चरण लोड करने के लिए Refresh actions चुनें।',
  },
  bn: {
    notStarted: 'প্ল্যানিং শুরু হয়নি।',
    refreshed: 'অ্যাকশনগুলো রিফ্রেশ হয়েছে: এখনকার পরের ধাপটি ব্যবহার করুন।',
    titleSaved: 'আপনার শিরোনাম সেভ হয়েছে, কিন্তু প্ল্যানিং শুরু করা যায়নি।',
    selectRefresh: 'সর্বশেষ পরের ধাপ লোড করতে Refresh actions নির্বাচন করুন।',
  },
};

export const startFailureCopy = (language?: string): StartFailureCopy => (
  START_FAILURE_COPY[(language ?? '').toLowerCase().slice(0, 2)] ?? START_FAILURE_COPY.en
);
